import { FC, useCallback, useState } from 'react';
import {
  Tooltip as MuiTooltip,
  TooltipProps as MuiTooltipProps,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import Typography from '../Typography';
import { TooltipProps } from './types';

const Tooltip: FC<React.PropsWithChildren<TooltipProps>> = ({
  children,
  disableHover = false,
  fullWidth = false,
  maxWidth = 260,
  placeCloseInDOM,
  PopperProps,
  tooltipContent,
  ...rest
}) => {
  const theme = useTheme();

  const [shadowRef, setShadowRef] = useState<HTMLDivElement | null>(null);
  const handleRef = useCallback((node: HTMLDivElement) => {
    setShadowRef(node);
  }, []);

  const popperProps: MuiTooltipProps['PopperProps'] = { ...PopperProps };
  if (placeCloseInDOM) {
    popperProps['container'] = shadowRef;
  }

  const TooltipTarget = fullWidth ? FullWidthTarget : ConstrainedWidthTarget;

  return (
    <>
      <MuiTooltip
        arrow
        disableInteractive={Boolean(disableHover)}
        PopperProps={popperProps}
        componentsProps={{
          popper: {
            sx: [
              {
                '& .MuiTooltip-arrow': {
                  color: theme.palette.colors.grey[800],
                },
                '& .MuiTooltip-tooltip': {
                  backgroundColor: theme.palette.colors.grey[800],
                  maxWidth,
                  padding: '8px 12px',
                  whiteSpace: 'pre-line',
                },
              },
            ],
          },
        }}
        title={
          tooltipContent ? (
            <Typography color={theme.palette.colors.white} variant='font14'>
              {tooltipContent}
            </Typography>
          ) : null
        }
        {...rest}
      >
        <TooltipTarget>
          {placeCloseInDOM && <div ref={handleRef}></div>}
          {children}
        </TooltipTarget>
      </MuiTooltip>
    </>
  );
};

/**
 * Use this styled component to fit the profile of the child component
 */
const ConstrainedWidthTarget = styled('div')`
  display: inline-flex;
  height: fit-content;
  width: fit-content;
  min-width: 0;
`;

const FullWidthTarget = styled('div')`
  width: 100%;
`;

export default Tooltip;
